import { zodResolver } from '@hookform/resolvers/zod';
import {
  TermOfServiceConsent,
  PrivacyNoticeConsent,
  SpecificUseConsent,
  isUnderThirteen,
  isUnderEighteen,
} from 'domains/User';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TemporaryRegistrationSchemaType } from 'commons/auditionForm';
import { useTemporaryRegistrationSchema } from './useTemporaryRegistrationSchema';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from 'templates/AppContextProvider';

export type UserRegistrationPageState =
  | {
      page: 'Top';
      onNext: () => void;
    }
  | {
      // age: Date
      page: 'AgeVerification';
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'UnderThirteenGuidance';
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'UnderEighteenGuidance';
      // onNext: () => void;
      auditionId: string;
      onBack: () => void;
    }
  | {
      page: 'TermOfServiceConsent';
      // consent: boolean
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'PrivacyNoticeConsent';
      // consent: boolean
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'TemporaryRegistration';
      // phoneNumber: string
      onBack: () => void;
    }
  | {
      page: 'SpecificUseConsent';
      // consent: boolean;
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'PrivacyPolicyConsent';
      // consent: boolean;
      onNext: () => void;
      onBack: () => void;
    }
  | {
      page: 'AuditionTermsConsent';
      // consent: boolean;
      onNext: (audition_revision: string) => void;
      onBack: () => void;
    }
  | {
      page: 'GroupRegistration';
      // consent: boolean;
      audition_revision: string;
      onBack: () => void;
    }
  | {
      page: 'PhoneVerification';
      // phoneNumber: string
      onNext: (phoneNumber: string) => void;
    }
  | {
      page: 'UserRegistration';
      phoneNumber: string;
      // form: User
    };

export const temporaryRegistrationDefaultValues: TemporaryRegistrationSchemaType = {
  phoneNumber: '',
  term_of_service_consent: TermOfServiceConsent.No,
  privacy_notice_consent: PrivacyNoticeConsent.No,
  specific_use_consent: SpecificUseConsent.No,
  birthday: null as unknown as Date,
};

// type UserRegistrationAction = {
//   type: "next"
// } | {
//   type: "back"
// }

/** 全体のページ遷移を定義する型から各ページのPropsを取得する
 *  最終的な型からpageは含まれないようにする
 * @example
 * type UserRegistrationState = {
 *  page: "Top"
 * } | {
 *  page: "Consent",
 *  value: boolean
 * } | {
 *  page: "Form",
 *  onSubmit: (value: string) => void
 * }
 * type TopProps = GetPageProps<UserRegistrationState, "Consent">
 * // TopProps = { value: boolean }
 */
export type GetPageProps<T extends { page: string }, K extends T['page']> = T extends { page: K }
  ? Omit<T, 'page'>
  : never;

export const useUserRegistrationHooks = (now_dtm: Date) => {
  //ページ遷移関連
  const [state, setState] = useState<UserRegistrationPageState>({} as UserRegistrationPageState);
  const [params] = useSearchParams();
  const sessionId = params.get('sessionId');
  const authStatus = params.get('status');
  const representative = params.get('representative');
  const { isGlobal } = useContext(AppContext);
  const initialTop = {
    page: 'Top',
    onNext: () => {
      setState(initialAgeVerification);
    },
  } satisfies UserRegistrationPageState;
  const initialAgeVerification = {
    page: 'AgeVerification',
    onNext: () => {
      // ホスト名でフローを分ける
      if (
        // 日本選択時のアカウント作成フロー
        !isGlobal &&
        isUnderThirteen(form.getValues('birthday') ?? null, now_dtm)
      ) {
        setState(initialUnderThirteenGuidance);
      } else if (
        // その他・台湾選択時のアカウント作成フロー
        isGlobal &&
        isUnderEighteen(form.getValues('birthday') ?? null, now_dtm)
      ) {
        setState(initialUnderEighteenGuidance);
      } else {
        setState(initialTermOfServiceConsent);
      }
    },
    onBack: () => {
      setState(initialTop);
    },
  } satisfies UserRegistrationPageState;
  const initialUnderEighteenGuidance = {
    page: 'UnderEighteenGuidance',
    auditionId: '',
    onBack: () => {
      setState(initialAgeVerification);
    },
  } satisfies UserRegistrationPageState;
  const initialUnderThirteenGuidance = {
    page: 'UnderThirteenGuidance',
    onNext: () => {
      setState(initialTermOfServiceConsent);
    },
    onBack: () => {
      setState(initialTop);
    },
  } satisfies UserRegistrationPageState;
  const initialTermOfServiceConsent = {
    page: 'TermOfServiceConsent',
    onNext: () => {
      setState(initialPrivacyNoticeConsent);
    },
    onBack: () => {
      if (isUnderThirteen(form.getValues('birthday') ?? null, now_dtm)) {
        setState(initialUnderThirteenGuidance);
      } else {
        setState(initialAgeVerification);
      }
    },
  } satisfies UserRegistrationPageState;
  const initialPrivacyNoticeConsent = {
    page: 'PrivacyNoticeConsent',
    onNext: () => {
      if (representative) {
        setState(initialSpecificUseConsent);
      } else {
        setState(initialTemporaryRegistration);
      }
    },
    onBack: () => {
      setState(initialTermOfServiceConsent);
    },
  } satisfies UserRegistrationPageState;
  const initialSpecificUseConsent = {
    page: 'SpecificUseConsent',
    onNext: () => {
      setState(initialPrivacyPolicyConsent);
    },
    onBack: () => {
      setState(initialPrivacyNoticeConsent);
    },
  } satisfies UserRegistrationPageState;
  const initialPrivacyPolicyConsent = {
    page: 'PrivacyPolicyConsent',
    onNext: () => {
      setState(initialAuditionTermsConsent);
    },
    onBack: () => {
      setState(initialPrivacyNoticeConsent);
    },
  } satisfies UserRegistrationPageState;
  const initialAuditionTermsConsent = {
    page: 'AuditionTermsConsent',
    onNext: (audition_revision: string) => {
      setState({
        ...initialGroupRegistration,
        audition_revision: audition_revision,
      });
    },
    onBack: () => {
      setState(initialPrivacyPolicyConsent);
    },
  } satisfies UserRegistrationPageState;
  const initialGroupRegistration = {
    page: 'GroupRegistration',
    audition_revision: '',
    onBack: () => {
      setState(initialAuditionTermsConsent);
    },
  } satisfies UserRegistrationPageState;
  const initialTemporaryRegistration = {
    page: 'TemporaryRegistration',
    onBack: () => {
      if (authStatus === 'approved') {
        setState(initialUnderEighteenGuidance);
      } else {
        setState(initialPrivacyNoticeConsent);
      }
    },
  } satisfies UserRegistrationPageState;
  const initialPhoneVerification = {
    page: 'PhoneVerification',
    onNext: (phoneNumber: string) => {
      setState({ ...initialUserRegistration, phoneNumber: phoneNumber });
    },
  } satisfies UserRegistrationPageState;
  const initialUserRegistration = {
    page: 'UserRegistration',
    phoneNumber: '',
  } satisfies UserRegistrationPageState;

  // setStateを参照できないので後からトップ画面をセット
  useEffect(() => {
    if (authStatus === 'approved' && sessionId) {
      setState(initialTemporaryRegistration);
    } else if (sessionId || (sessionId && authStatus === 'phoneVerification')) {
      setState(initialPhoneVerification);
    } else if (representative) {
      setState(initialAgeVerification);
    } else {
      setState(initialTop);
    }
  }, []);

  //フォーム関連
  const TemporaryRegistrationSchema = useTemporaryRegistrationSchema();
  const form = useForm<TemporaryRegistrationSchemaType>({
    resolver: zodResolver(TemporaryRegistrationSchema),
    shouldFocusError: true,
    defaultValues: temporaryRegistrationDefaultValues,
  });

  return {
    ...state,
    ...form,
  };
};
export type UserRegistrationState = ReturnType<typeof useUserRegistrationHooks>;

// type UserRegistrationContext = ReturnType<typeof useUserRegistrationHooks>
// const UserRegistrationContext = createContext<UserRegistrationContext>({} as UserRegistrationContext);

// export const useUserRegistrationContext = () => useContext(UserRegistrationContext)
// export const UserRegistrationProvider:FC<PropsWithChildren> = (props) => {
//   const state = useUserRegistrationHooks()
//   return (
//     <UserRegistrationContext.Provider value={state}>
//       {props.children}
//     </UserRegistrationContext.Provider>
//   )
// }
